/**
 * Edit User Page
 *
 * Form for editing an existing platform user.
 * Only accessible by ADMIN and SADMIN roles.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save } from 'lucide-react';

import {
  Button,
  Input,
  Select,
  MultiSelect,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Loader,
} from '@/components/common';
import { userService } from '@/api/services';
import type { UpdateUserRequest, UserDetails, Gender, UserStatus } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { useRoleOptions } from '@/hooks/useRoleOptions';

// ============================================
// Form Schema
// ============================================

const editUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email address'),
  mobile: z.string().min(10, 'Mobile number must be at least 10 digits'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional().nullable(),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
  userStatus: z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED', 'INACTIVE']),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

// ============================================
// Options
// ============================================

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'TERMINATED', label: 'Terminated' },
];

// ============================================
// Page Component
// ============================================

export function EditUserPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  // Roles are loaded from the RBAC catalog (GET /api/rbac/roles).
  const { roleOptions } = useRoleOptions();

  const [user, setUser] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
  });

  const selectedRoles = watch('roles') || [];
  const selectedGender = watch('gender');
  const selectedStatus = watch('userStatus');

  // Fetch user data
  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const userData = await userService.getUserById(id);
        setUser(userData);

        // Reset form with user data
        reset({
          firstName: userData.firstName,
          lastName: userData.lastName || '',
          email: userData.email,
          mobile: userData.mobile,
          gender: userData.gender as Gender | undefined,
          roles: userData.listOfRoles || [],
          userStatus: userData.userStatus,
        });
      } catch (error) {
        console.error('Failed to fetch user:', error);
        toast.error('Failed to load user');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async (data: EditUserFormData) => {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const request: UpdateUserRequest = {
        id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobile: data.mobile,
        gender: data.gender as Gender | undefined,
        listOfRoles: data.roles,
        userStatus: data.userStatus as UserStatus,
      };

      await userService.updateUser(request);
      toast.success('User updated successfully');
      navigate(`/users/${id}`);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading user..." />
      </div>
    );
  }

  // Not found state
  if (!user) {
    return (
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground dark:text-foreground-dark">
          User not found
        </h2>
        <Button className="mt-4" onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/users/${id}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Edit User
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Update information for @{user.username}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader
            title="User Information"
            description="Update the user details below"
          />
          <CardBody className="space-y-6">
            {/* Username (read-only) */}
            <Input
              label="Username"
              value={user.username}
              disabled
              helperText="Username cannot be changed"
            />

            {/* Name */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="First Name"
                placeholder="Enter first name"
                required
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                label="Last Name"
                placeholder="Enter last name"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            {/* Email & Mobile */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="Email"
                type="email"
                placeholder="Enter email address"
                required
                error={errors.email?.message}
                {...register('email')}
              />
              <Input
                label="Mobile Number"
                placeholder="Enter mobile number"
                required
                error={errors.mobile?.message}
                {...register('mobile')}
              />
            </div>

            {/* Gender & Status */}
            <div className="grid gap-6 md:grid-cols-2">
              <Select
                label="Gender"
                placeholder="Select gender"
                options={GENDER_OPTIONS}
                value={selectedGender || undefined}
                onChange={(value) => setValue('gender', value as Gender)}
              />
              <Select
                label="Status"
                placeholder="Select status"
                required
                options={STATUS_OPTIONS}
                value={selectedStatus}
                onChange={(value) => setValue('userStatus', value as UserStatus)}
                error={!!errors.userStatus}
                errorMessage={errors.userStatus?.message}
              />
            </div>

            {/* Roles */}
            <MultiSelect
              label="Roles"
              placeholder="Select roles"
              required
              options={roleOptions}
              value={selectedRoles}
              onChange={(values) => setValue('roles', values, { shouldValidate: true })}
              errorMessage={errors.roles?.message}
            />
          </CardBody>

          <CardFooter className="justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/users/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Changes
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}

export default EditUserPage;
